import { Component, computed, inject, input, linkedSignal, model, output, signal } from '@angular/core';
import { forkJoin, of } from 'rxjs';

import { ButtonModule } from '@openng/optimus-ui/button';
import { SkeletonModule } from '@openng/optimus-ui/skeleton';

import { DropdownData, rxResourceFixed } from '@zayan-erp/shared';

import { DsmAppointmentDealScenario, DsmAppointmentStep } from '../../../models/dsm-appointment-scenario.models';
import { DsmProductOption, DsmSelectedProductEntry } from '../../../models/dsm-deal-options.models';
import { DsmProductLibraryCategory, DsmProductLibrarySubcategory } from '../../../models/dsm-product-library.models';
import { DsmApsDealContextService } from '../../../services/dsm-aps-deal-context.service';
import { DsmAppointmentScenariosService } from '../../../services/dsm-appointment-scenarios.service';
import { DsmProductLibraryService } from '../../../services/dsm-product-library.service';
import {
  DsmAddOptionOptionsPanelComponent,
  DsmScopedSubcategoryOption
} from '../dsm-add-option-options-panel/dsm-add-option-options-panel.component';

/** The step's browsing scope: its subcategory expansion plus the DSM labels of their parents. */
interface DsmStepScope {
  subcategories: DsmProductLibrarySubcategory[];
  categories: DsmProductLibraryCategory[];
}

/** The two phases the tab walks through: pick a step, then browse the options that step's
 *  categories and subcategories resolve to. */
export type DsmScenarioTabPhase = 'step' | 'options';

@Component({
  selector: 'dsm-add-option-scenarios-tab',
  standalone: true,
  imports: [ButtonModule, SkeletonModule, DsmAddOptionOptionsPanelComponent],
  templateUrl: './dsm-add-option-scenarios-tab.component.html'
})
export class DsmAddOptionScenariosTabComponent {
  readonly #dealContext = inject(DsmApsDealContextService);
  readonly #scenariosService = inject(DsmAppointmentScenariosService);
  readonly #libraryService = inject(DsmProductLibraryService);

  readonly dealId = computed(() => this.#dealContext.effectiveDealId());

  readonly modelId = input.required<string>();
  readonly elevationId = input.required<string>();
  readonly addendumId = input.required<string>();
  readonly lotId = input.required<string | null>();
  readonly addedProductIds = input.required<string[]>();
  /** True while the Scenarios tab is the selected one. */
  readonly active = input(false);

  /** The host's working set and layout state, passed straight through to the options panel. */
  readonly selectedEntries = input<DsmSelectedProductEntry[]>([]);
  readonly sidebarPinned = input(false);
  readonly filtersVisible = model<boolean>(true);

  /** A picked product goes back to the modal untouched, so it runs the same add flow as the
   *  Options tab — location, attributes, kits and all. */
  readonly productPicked = output<DsmProductOption>();

  readonly defaultStepImageUrl = 'assets/images/img-placeholder.png';

  /** Latches on the tab's first activation, so the deal's scenarios are read once the user
   *  actually opens the tab and the steps then survive switching away and back. */
  readonly #everActive = linkedSignal<boolean, boolean>({
    source: this.active,
    computation: (isActive, previous) => isActive || (previous?.value ?? false)
  });

  readonly phase = signal<DsmScenarioTabPhase>('step');
  readonly selectedStepId = signal<string | null>(null);

  readonly scenariosResource = rxResourceFixed({
    params: () => ({ dealId: this.#everActive() ? this.dealId() : null }),
    defaultValue: [] as DsmAppointmentDealScenario[],
    stream: ({ params }) => {
      if (!params.dealId) return of([] as DsmAppointmentDealScenario[]);
      return this.#scenariosService.readScenariosByDeal(params.dealId);
    }
  });

  readonly scenariosLoading = computed(() => this.scenariosResource.isLoading());
  readonly scenariosHasError = computed(() => !!this.scenariosResource.error());

  /**
   * The one scenario that applies to this deal. Several can be assigned to a deal's scope, but the
   * backend already resolves them — the most specific tier wins — and marks the outcome with
   * `isWinner`, so there is nothing here for the user to choose. Position is the documented
   * fallback: the list comes back most specific first.
   */
  readonly activeScenario = computed<DsmAppointmentDealScenario | null>(() => {
    const scenarios = this.scenariosResource.value() ?? [];
    return scenarios.find(scenario => scenario.isWinner) ?? scenarios[0] ?? null;
  });

  readonly steps = computed<DsmAppointmentStep[]>(() => this.activeScenario()?.steps ?? []);

  readonly selectedStep = computed<DsmAppointmentStep | null>(() => {
    const stepId = this.selectedStepId();
    if (!stepId) return null;
    return this.steps().find(step => step.id === stepId) ?? null;
  });

  /** 1-based position of the open step, so its heading can say which step it is. */
  readonly selectedStepNumber = computed(() => {
    const stepId = this.selectedStepId();
    if (!stepId) return 0;
    return this.steps().findIndex(step => step.id === stepId) + 1;
  });

  /**
   * What the picked step covers, in DSM's own words. The step expansion gives the subcategories
   * with their DSM labels and parent category; the library read gives the DSM label for each
   * parent category, which the expansion does not carry. Both exclude hidden and retired rows,
   * so a customer sees what the appointment would actually offer.
   */
  readonly stepScopeResource = rxResourceFixed({
    params: () => ({
      scenarioId: this.activeScenario()?.id ?? null,
      stepId: this.selectedStepId(),
      productListId: this.activeScenario()?.productListId ?? null
    }),
    defaultValue: { subcategories: [], categories: [] } as DsmStepScope,
    stream: ({ params }) => {
      if (!params.scenarioId || !params.stepId || !params.productListId) {
        return of<DsmStepScope>({ subcategories: [], categories: [] });
      }
      return forkJoin({
        subcategories: this.#scenariosService.readStepSubcategories(params.scenarioId, params.stepId),
        categories: this.#libraryService.readCategories(params.productListId, false, false, false)
      });
    }
  });

  readonly stepScope = computed<DsmStepScope>(
    () => this.stepScopeResource.value() ?? { subcategories: [], categories: [] }
  );
  readonly stepScopeLoading = computed(() => this.stepScopeResource.isLoading());
  readonly stepScopeHasError = computed(() => !!this.stepScopeResource.error());

  readonly stepSubcategoryOptions = computed<DsmScopedSubcategoryOption[]>(() =>
    this.stepScope().subcategories.map(row => ({
      id: row.subcategoryId,
      firstLabel: row.effectiveLabel,
      categoryId: row.categoryId
    }))
  );

  readonly stepCategoryOptions = computed<DropdownData[]>(() => {
    const scope = this.stepScope();
    const labels = new Map(scope.categories.map(category => [category.categoryId, category.effectiveLabel]));
    const orderedIds = [...new Set(scope.subcategories.map(row => row.categoryId).filter((id): id is string => !!id))];
    // A parent missing from the library read is one DSM has hidden or retired, so leave it out
    // rather than fall back to showing its technical library name.
    return orderedIds.filter(id => labels.has(id)).map(id => ({ id, firstLabel: labels.get(id) as string }));
  });

  readonly stepHasScope = computed(() => this.stepCategoryOptions().length > 0);

  selectStep(stepId: string): void {
    this.selectedStepId.set(stepId);
    this.phase.set('options');
  }

  backToSteps(): void {
    this.selectedStepId.set(null);
    this.phase.set('step');
  }

  retryScenarios(): void {
    this.scenariosResource.reload();
  }

  retryStepScope(): void {
    this.stepScopeResource.reload();
  }

  /**
   * How many options the step's members hold. This is the library-wide count that came with the
   * step, so it is a size hint rather than a promise of what this lot will offer — the products
   * call still filters by model and elevation.
   */
  stepOptionCountLabel(step: DsmAppointmentStep): string {
    const count = step.members.reduce((total, member) => total + (member.activeProductCount ?? 0), 0);
    return `${count} ${count === 1 ? 'option' : 'options'}`;
  }

  hasStepImage(step: DsmAppointmentStep): boolean {
    return !!step.image?.directUrl;
  }

  /** `directUrl` is a short-lived pre-signed S3 URL, so it is read straight off the payload
   *  each render rather than cached. */
  stepImageUrl(step: DsmAppointmentStep): string {
    return step.image?.directUrl ?? this.defaultStepImageUrl;
  }
}
